import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Service } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { environment } from '@environments/environment'
import { InvoiceOutcome, InvoiceStatus, InvoiceUpload } from '../../models/tickets.model'

/** El token de homologación va en la cabecera, no en el cuerpo. */
const TOKEN_HEADER = 'X-Pernod-Homolog-Token'

/** Con este estado la lambda confirma que la factura quedó cargada. */
const REGISTERED = 'REGISTERED'

/** Con este, la validación no llegó a una conclusión y no hay nada que reenviar. */
const NOT_VALIDATED = 'NOT_VALIDATED'

/** HTTP de la lambda traducido a lo que el micrositio hace después. */
const STATUS_BY_CODE: Record<number, InvoiceStatus> = {
  202: 'review',
  409: 'conflict',
  422: 'rejected',
  503: 'retry'
}

/**
 * Respuesta del contrato `upload-validation-v3`. La lambda cuenta cada etapa por
 * separado: valida la factura, la sube y la registra en el centro de consumo.
 */
interface UploadResponse {
  contract_version?: string
  submission_id?: string
  message?: string
  /** Presente cuando la petición ni siquiera llegó a validarse. */
  error?: { status?: string; code?: string } | null
  validation?: {
    status?: string
    reason_codes?: string[]
    next_action?: string
    /** El `submission_id` de la carga anterior si esta es la misma factura. */
    duplicate_of?: string | null
  } | null
  registration?: { status?: string; stage?: string } | null
}

/**
 * HTTP puro de los tickets y única pieza que sabe de dónde salen: una lambda
 * propia, no la API de Superlikers. Ella valida la factura y la carga al centro
 * de consumo del gerente.
 *
 * Nunca lanza por la respuesta de la lambda: su HTTP y sus estados son parte del
 * resultado y dicen si se puede reintentar. Solo lanza si la petición se rompe.
 */
@Service()
export class TicketsApi {
  readonly #http = inject(HttpClient)

  async uploadInvoice(invoice: InvoiceUpload): Promise<InvoiceOutcome> {
    const body = {
      request_id: invoice.requestId,
      uid: invoice.uid,
      campaign_id: environment.id,
      image: { content_type: invoice.contentType, base64: invoice.base64 }
    }

    try {
      // Se observa la respuesta completa porque un 202 también entra por aquí y
      // no significa lo mismo que un 200.
      const response = await firstValueFrom(this.#http.post<UploadResponse>(environment.invoicesUrl, body, { headers: { [TOKEN_HEADER]: environment.invoicesToken }, observe: 'response' }))

      return this.#outcome(STATUS_BY_CODE[response.status] ?? this.#statusOf(response.body), response.body)
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error

      // Un 0 es un fallo de red: se reintenta igual que un 503.
      return this.#outcome(STATUS_BY_CODE[error.status] ?? 'retry', error.error)
    }
  }

  /**
   * Con un 200 el HTTP no alcanza: lo que decide es en qué quedaron la validación
   * y el registro.
   */
  #statusOf(response: UploadResponse | null): InvoiceStatus {
    if (response?.registration?.status === REGISTERED) return 'registered'
    if (response?.validation?.duplicate_of) return 'duplicate'

    // Sin conclusión no hay nada que corregir ni que reenviar: queda en revisión.
    return response?.validation?.status === NOT_VALIDATED ? 'review' : 'rejected'
  }

  #outcome(status: InvoiceStatus, response: UploadResponse | null): InvoiceOutcome {
    return {
      status,
      message: response?.message?.trim() || '',
      reasonCodes: response?.validation?.reason_codes ?? this.#codesOf(response),
      nextAction: response?.validation?.next_action ?? '',
      submissionId: response?.submission_id ?? ''
    }
  }

  /** Sin validación, el motivo viene en `error`: sirve igual para el log. */
  #codesOf(response: UploadResponse | null): string[] {
    const error = response?.error

    return [error?.code, error?.status].filter((code): code is string => Boolean(code))
  }
}
